import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, MessageSquare, ThumbsUp, Send, Tag } from 'lucide-react';
import { toast } from 'sonner';

const API = `/api`;

const CATEGORIES = [
  { value: 'all', label: 'All', color: 'bg-gray-100 text-gray-700' },
  { value: 'feature_request', label: 'Feature Request', color: 'bg-blue-100 text-blue-700' },
  { value: 'bug_report', label: 'Bug Report', color: 'bg-red-100 text-red-700' },
  { value: 'general', label: 'General', color: 'bg-green-100 text-green-700' }
];

const getCategoryStyle = (cat) => {
  const found = CATEGORIES.find(c => c.value === cat);
  return found ? found.color : 'bg-gray-100 text-gray-700';
};

const getCategoryLabel = (cat) => {
  const found = CATEGORIES.find(c => c.value === cat);
  return found ? found.label : cat;
};

const FeedbackBoard = ({ slpData }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [newMessage, setNewMessage] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/feedback/board`, { withCredentials: true });
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to load feedback:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleSubmit = async () => {
    if (!newMessage.trim()) {
      toast.error('Please enter your feedback');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        message: newMessage,
        category: newCategory
      }, { withCredentials: true });
      toast.success('Feedback posted!');
      setNewMessage('');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to post feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpvote = async (feedbackId) => {
    try {
      const response = await axios.post(`${API}/feedback/upvote/${feedbackId}`, {}, { withCredentials: true });
      setPosts(prev => prev.map(p =>
        p.id === feedbackId
          ? { ...p, upvote_count: response.data.upvote_count, upvoted: response.data.upvoted }
          : p
      ));
    } catch (error) {
      toast.error('Failed to upvote');
    }
  };

  const filteredPosts = activeCategory === 'all'
    ? posts
    : posts.filter(p => p.category === activeCategory);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: 'var(--fe-bg)' }}>
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <button
            data-testid="back-to-dashboard-from-feedback"
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#2C4C3B] rounded-2xl flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Community Feedback</h1>
              <p className="text-sm text-gray-600">Share ideas, report issues, vote on what matters</p>
            </div>
          </div>
        </div>

        {/* New Post */}
        <div className="bg-white rounded-3xl shadow-lg border border-white/50 p-6 mb-6">
          <textarea
            data-testid="feedback-board-textarea"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Share a suggestion, report a bug, or give general feedback..."
            rows={3}
            className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E5E5] bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[#2C4C3B]/20 focus:border-[#2C4C3B] outline-none transition-all resize-none"
          />
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-gray-500" />
              {CATEGORIES.filter(c => c.value !== 'all').map((cat) => (
                <button
                  key={cat.value}
                  data-testid={`category-select-${cat.value}`}
                  onClick={() => setNewCategory(cat.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    newCategory === cat.value
                      ? cat.color + ' ring-2 ring-offset-1 ring-gray-300'
                      : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            <button
              data-testid="feedback-board-submit"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2C4C3B] text-white font-medium hover:bg-[#1E3629] transition-all disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              data-testid={`filter-${cat.value}`}
              onClick={() => setActiveCategory(cat.value)}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                activeCategory === cat.value
                  ? 'bg-[#2C4C3B] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-[#E5E5E5]'
              }`}
            >
              {cat.label}
              {cat.value !== 'all' && (
                <span className="ml-1.5 text-xs opacity-75">
                  ({posts.filter(p => p.category === cat.value).length})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Posts */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-[#2C4C3B] border-t-transparent rounded-full animate-spin" />
            <p className="mt-4 text-gray-500">Loading feedback...</p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-3xl shadow-sm border border-[#E5E5E5]">
            <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No feedback yet in this category</p>
            <p className="text-sm text-gray-400 mt-1">Be the first to share your thoughts!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPosts.map((post) => (
              <div
                key={post.id}
                data-testid={`feedback-post-${post.id}`}
                className="bg-white rounded-2xl shadow-sm border border-[#E5E5E5] p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex gap-4">
                  <button
                    data-testid={`upvote-${post.id}`}
                    onClick={() => handleUpvote(post.id)}
                    className={`flex flex-col items-center gap-1 px-2 py-1 rounded-xl transition-all ${
                      post.upvoted
                        ? 'bg-[#2C4C3B]/10 text-[#2C4C3B]'
                        : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                    }`}
                  >
                    <ThumbsUp className={`w-5 h-5 ${post.upvoted ? 'fill-current' : ''}`} />
                    <span className="text-sm font-bold">{post.upvote_count || 0}</span>
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-800 leading-relaxed">{post.message}</p>
                    <div className="flex items-center gap-3 mt-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getCategoryStyle(post.category)}`}>
                        {getCategoryLabel(post.category)}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatDate(post.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackBoard;
